import { useCallback, useEffect, useState } from "react";
import api, { apiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { AlertTriangle, CalendarCheck } from "lucide-react";
import { SourceChip } from "../components/PipelineBits";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import { inputCls } from "../hooks/useMasterMaps";

function JoinerCard({ r, canWrite, onJoined, busy }) {
  return (
    <div data-testid={`joiner-card-${r.application_id}`}
      className={`bg-white border rounded-sm p-4 ${r.no_show ? "border-[#BB162B]/60" : "border-gray-200"}`}>
      <div className="flex items-start justify-between">
        <div className="text-sm font-medium text-[#0E2240]">{r.candidate_name}</div>
        {r.no_show && (
          <span data-testid={`noshow-flag-${r.application_id}`} className="inline-flex items-center gap-1 text-[9px] uppercase text-white bg-[#BB162B] px-1.5 py-0.5 rounded-sm">
            <AlertTriangle size={9} /> Did not join
          </span>
        )}
      </div>
      <div className="text-xs text-[#6B7280] mt-0.5">{r.desig_name} · {r.branch_name} · {r.req_no}</div>
      <div className="mt-2 flex items-center justify-between text-xs">
        <SourceChip source={r.source} />
        <span className="tabular-nums text-[#0E2240]">DOJ: <span className={r.no_show ? "text-[#BB162B] font-semibold" : "font-medium"}>{r.expected_doj}</span></span>
      </div>
      <div className="text-[10px] text-[#6B7280] mt-1 tabular-nums">₹{Number(r.offered_ctc).toLocaleString()} · stage: {r.current_stage}</div>
      {canWrite && (
        <button data-testid={`mark-joined-${r.application_id}`} disabled={busy} onClick={() => onJoined(r)}
          className="mt-3 w-full py-2 text-xs bg-[#1B7F3B] text-white rounded-sm hover:bg-[#166832] transition-colors duration-200 disabled:opacity-50">
          Mark joined
        </button>
      )}
    </div>
  );
}

export default function JoiningTracker() {
  const { can } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [joinDlg, setJoinDlg] = useState(null);
  const [joinDate, setJoinDate] = useState("");

  const load = useCallback(() => {
    setLoading(true);
    api.get("/joining-tracker").then(({ data }) => setRows(data))
      .catch((e) => toast.error(apiError(e))).finally(() => setLoading(false));
  }, []);
  useEffect(load, [load]);

  const onJoined = (r) => {
    setJoinDlg(r);
    setJoinDate(new Date().toISOString().slice(0, 10));
  };

  const confirmJoined = async () => {
    if (!joinDate) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/applications/${joinDlg.application_id}/joined`, { joined_on: joinDate });
      toast.success(`${joinDlg.candidate_name} joined — employee ${data.employee_code} created, requisition updated`);
      setJoinDlg(null); load();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const noShows = rows.filter((r) => r.no_show);
  const thisWeek = rows.filter((r) => !r.no_show && r.this_week);
  const later = rows.filter((r) => !r.no_show && !r.this_week);
  const canWrite = can("pipeline", "write");

  return (
    <div data-testid="joining-page">
      <h1 className="font-serif text-2xl sm:text-3xl text-[#0E2240] tracking-tight">Joining Tracker</h1>
      <p className="text-sm text-[#6B7280] mt-1 mb-8">Offer-accepted candidates and their dates of joining — no-shows go red immediately.</p>

      {loading ? (
        <div className="bg-white border border-gray-200 rounded-sm p-10 text-center text-sm text-[#6B7280]">Loading…</div>
      ) : (
        <>
          <h2 className="font-serif text-lg text-[#BB162B] mb-3">Did not join ({noShows.length})</h2>
          {noShows.length === 0 ? (
            <div className="bg-white border border-gray-200 rounded-sm p-5 text-sm text-[#6B7280] mb-8">No missed joining dates.</div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 mb-8" data-testid="noshow-list">
              {noShows.map((r) => <JoinerCard key={r.application_id} r={r} canWrite={canWrite} onJoined={onJoined} busy={busy} />)}
            </div>
          )}

          <h2 className="font-serif text-lg text-[#0E2240] mb-3 flex items-center gap-2"><CalendarCheck size={17} /> Expected this week ({thisWeek.length})</h2>
          {thisWeek.length === 0 ? (
            <div className="bg-white border border-gray-200 rounded-sm p-5 text-sm text-[#6B7280] mb-8">No joiners this week.</div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 mb-8" data-testid="thisweek-list">
              {thisWeek.map((r) => <JoinerCard key={r.application_id} r={r} canWrite={canWrite} onJoined={onJoined} busy={busy} />)}
            </div>
          )}

          {later.length > 0 && (
            <>
              <h2 className="font-serif text-lg text-[#0E2240] mb-3">Later ({later.length})</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="later-list">
                {later.map((r) => <JoinerCard key={r.application_id} r={r} canWrite={canWrite} onJoined={onJoined} busy={busy} />)}
              </div>
            </>
          )}
        </>
      )}

      <Dialog open={!!joinDlg} onOpenChange={(o) => !o && setJoinDlg(null)}>
        {joinDlg && (
          <DialogContent className="bg-white rounded-sm shadow-none border border-gray-200 max-w-sm" data-testid="join-dialog">
            <DialogHeader>
              <DialogTitle className="font-serif text-[#0E2240]">Mark joined — {joinDlg.candidate_name}</DialogTitle>
              <DialogDescription className="text-xs text-[#6B7280]">
                Creates the employee record, links it to {joinDlg.req_no} and updates the position count.
              </DialogDescription>
            </DialogHeader>
            <div className="py-1">
              <label className="text-xs text-[#6B7280] block mb-1">Actual date of joining</label>
              <input data-testid="join-date" type="date" className={inputCls} value={joinDate} onChange={(e) => setJoinDate(e.target.value)} />
            </div>
            <DialogFooter>
              <button onClick={() => setJoinDlg(null)} className="px-4 py-2 text-sm border border-gray-300 rounded-sm hover:bg-gray-50">Cancel</button>
              <button data-testid="join-confirm" disabled={busy || !joinDate} onClick={confirmJoined}
                className="px-4 py-2 text-sm bg-[#1B7F3B] text-white rounded-sm hover:bg-[#166832] disabled:opacity-50">Confirm joining</button>
            </DialogFooter>
          </DialogContent>
        )}
      </Dialog>
    </div>
  );
}
